import Vue from 'vue'
import Meta from 'vue-meta'
import ClientOnly from 'vue-client-only'
import NoSsr from 'vue-no-ssr'
import { createRouter } from './router.js'
import NuxtChild from './components/nuxt-child.js'
import NuxtError from '../layouts/error.vue'
import Nuxt from './components/nuxt.js'
import App from './App.js'
import { setContext, getLocation, getRouteData, normalizeError } from './utils'
import { createStore } from './store.js'

/* Plugins */

import nuxt_plugin_bootstrapvue_12f4445b from 'nuxt_plugin_bootstrapvue_12f4445b' // Source: ./bootstrap-vue.js (mode: 'all')
import nuxt_plugin_sentryserver_4465b420 from 'nuxt_plugin_sentryserver_4465b420' // Source: ./sentry.server.js (mode: 'server')
import nuxt_plugin_sentryclient_7813b468 from 'nuxt_plugin_sentryclient_7813b468' // Source: ./sentry.client.js (mode: 'client')
import nuxt_plugin_axios_2e0a522c from 'nuxt_plugin_axios_2e0a522c' // Source: ./axios.js (mode: 'all')
import nuxt_plugin_polyfills_a2ea40e0 from 'nuxt_plugin_polyfills_a2ea40e0' // Source: ../plugins/polyfills (mode: 'client')
import nuxt_plugin_vcalendar_106a77a9 from 'nuxt_plugin_vcalendar_106a77a9' // Source: ../plugins/v-calendar (mode: 'client')
import nuxt_plugin_vmask_42ab70d7 from 'nuxt_plugin_vmask_42ab70d7' // Source: ../plugins/v-mask (mode: 'client')
import nuxt_plugin_vueawesomeswiper_0f4ed586 from 'nuxt_plugin_vueawesomeswiper_0f4ed586' // Source: ../plugins/vue-awesome-swiper (mode: 'client')
import nuxt_plugin_veevalidation_53a8a3f8 from 'nuxt_plugin_veevalidation_53a8a3f8' // Source: ../plugins/vee-validation (mode: 'client')
import nuxt_plugin_vueloadingoverlay_7cb58804 from 'nuxt_plugin_vueloadingoverlay_7cb58804' // Source: ../plugins/vue-loading-overlay (mode: 'client')
import nuxt_plugin_vuexlocalStorage_4946c952 from 'nuxt_plugin_vuexlocalStorage_4946c952' // Source: ../plugins/vuex-localStorage (mode: 'client')
import nuxt_plugin_bootstrapvue_8290879a from 'nuxt_plugin_bootstrapvue_8290879a' // Source: ../plugins/bootstrap-vue (mode: 'client')
import nuxt_plugin_vuebootstraptypeahead_0ae22a26 from 'nuxt_plugin_vuebootstraptypeahead_0ae22a26' // Source: ../plugins/vue-bootstrap-typeahead (mode: 'client')
import nuxt_plugin_coophelper_1787988c from 'nuxt_plugin_coophelper_1787988c' // Source: ../plugins/coop-helper (mode: 'all')
import nuxt_plugin_matomo_362ffbe5 from 'nuxt_plugin_matomo_362ffbe5' // Source: ../plugins/matomo (mode: 'client')
import nuxt_plugin_sentryconfig_2fbb3908 from 'nuxt_plugin_sentryconfig_2fbb3908' // Source: ../plugins/sentry-config (mode: 'client')
import nuxt_plugin_vuemultiselect_f82b3d8c from 'nuxt_plugin_vuemultiselect_f82b3d8c' // Source: ../plugins/vue-multiselect (mode: 'client')

// Component: <ClientOnly>
Vue.component(ClientOnly.name, ClientOnly)
// TODO: Remove in Nuxt 3: <NoSsr>
Vue.component(NoSsr.name, {
  ...NoSsr,
  render(h, ctx) {
    if (process.client && !NoSsr._warned) {
      NoSsr._warned = true
      console.warn(`<no-ssr> has been deprecated and will be removed in Nuxt 3, please use <client-only> instead`)
    }
    return NoSsr.render(h, ctx)
  }
})

// Component: <NuxtChild>
Vue.component(NuxtChild.name, NuxtChild)
Vue.component('NChild', NuxtChild)

// Component NuxtLink is imported in server.js or client.js

// Component: <Nuxt>`
Vue.component(Nuxt.name, Nuxt)

// vue-meta configuration
Vue.use(Meta, {
  keyName: 'head', // the component option name that vue-meta looks for meta info on.
  attribute: 'data-n-head', // the attribute name vue-meta adds to the tags it observes
  ssrAttribute: 'data-n-head-ssr', // the attribute name that lets vue-meta know that meta info has already been server-rendered
  tagIDKeyName: 'hid' // the property name that vue-meta uses to determine whether to overwrite or append a tag
})

const defaultTransition = {"name":"page","mode":"out-in","appear":true,"appearClass":"appear","appearActiveClass":"appear-active","appearToClass":"appear-to"}

async function createApp(ssrContext) {
  const router = await createRouter(ssrContext)

  const store = createStore(ssrContext)
  // Add this.$router into store actions/mutations
  store.$router = router

  // Create Root instance

  // here we inject the router and store to all child components,
  // making them available everywhere as `this.$router` and `this.$store`.
  const app = {
    router,
    store,
    nuxt: {
      defaultTransition,
      transitions: [ defaultTransition ],
      setTransitions(transitions) {
        if (!Array.isArray(transitions)) {
          transitions = [ transitions ]
        }
        transitions = transitions.map((transition) => {
          if (!transition) {
            transition = defaultTransition
          } else if (typeof transition === 'string') {
            transition = Object.assign({}, defaultTransition, { name: transition })
          } else {
            transition = Object.assign({}, defaultTransition, transition)
          }
          return transition
        })
        this.$options.nuxt.transitions = transitions
        return transitions
      },
      err: null,
      dateErr: null,
      error(err) {
        err = err || null
        app.context._errored = Boolean(err)
        err = err ? normalizeError(err) : null
        const nuxt = this.nuxt || this.$options.nuxt
        nuxt.dateErr = Date.now()
        nuxt.err = err
        // Used in src/server.js
        if (ssrContext) ssrContext.nuxt.error = err
        return err
      }
    },
    ...App
  }

  // Make app available into store via this.app
  store.app = app

  const next = ssrContext ? ssrContext.next : location => app.router.push(location)
  // Resolve route
  let route
  if (ssrContext) {
    route = router.resolve(ssrContext.url).route
  } else {
    const path = getLocation(router.options.base)
    route = router.resolve(path).route
  }

  // Set context to app.context
  await setContext(app, {
    route,
    next,
    error: app.nuxt.error.bind(app),
    store,
    payload: ssrContext ? ssrContext.payload : undefined,
    req: ssrContext ? ssrContext.req : undefined,
    res: ssrContext ? ssrContext.res : undefined,
    beforeRenderFns: ssrContext ? ssrContext.beforeRenderFns : undefined,
    ssrContext
  })

  const inject = function (key, value) {
    if (!key) throw new Error('inject(key, value) has no key provided')
    if (typeof value === 'undefined') throw new Error('inject(key, value) has no value provided')
    key = '$' + key
    // Add into app
    app[key] = value

    // Add into store
    store[key] = app[key]

    // Check if plugin not already installed
    const installKey = '__nuxt_' + key + '_installed__'
    if (Vue[installKey]) return
    Vue[installKey] = true
    // Call Vue.use() to install the plugin into vm
    Vue.use(() => {
      if (!Vue.prototype.hasOwnProperty(key)) {
        Object.defineProperty(Vue.prototype, key, {
          get() {
            return this.$root.$options[key]
          }
        })
      }
    })
  }

  if (process.client) {
    // Replace store state before plugins execution
    if (window.__NUXT__ && window.__NUXT__.state) {
      store.replaceState(window.__NUXT__.state)
    }
  }

  // Plugin execution

  if (typeof nuxt_plugin_bootstrapvue_12f4445b === 'function') {
    await nuxt_plugin_bootstrapvue_12f4445b(app.context, inject)
  }

  if (process.server && typeof nuxt_plugin_sentryserver_4465b420 === 'function') {
    await nuxt_plugin_sentryserver_4465b420(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryclient_7813b468 === 'function') {
    await nuxt_plugin_sentryclient_7813b468(app.context, inject)
  }

  if (typeof nuxt_plugin_axios_2e0a522c === 'function') {
    await nuxt_plugin_axios_2e0a522c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_polyfills_a2ea40e0 === 'function') {
    await nuxt_plugin_polyfills_a2ea40e0(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vcalendar_106a77a9 === 'function') {
    await nuxt_plugin_vcalendar_106a77a9(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vmask_42ab70d7 === 'function') {
    await nuxt_plugin_vmask_42ab70d7(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueawesomeswiper_0f4ed586 === 'function') {
    await nuxt_plugin_vueawesomeswiper_0f4ed586(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_veevalidation_53a8a3f8 === 'function') {
    await nuxt_plugin_veevalidation_53a8a3f8(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vueloadingoverlay_7cb58804 === 'function') {
    await nuxt_plugin_vueloadingoverlay_7cb58804(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuexlocalStorage_4946c952 === 'function') {
    await nuxt_plugin_vuexlocalStorage_4946c952(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_bootstrapvue_8290879a === 'function') {
    await nuxt_plugin_bootstrapvue_8290879a(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuebootstraptypeahead_0ae22a26 === 'function') {
    await nuxt_plugin_vuebootstraptypeahead_0ae22a26(app.context, inject)
  }

  if (typeof nuxt_plugin_coophelper_1787988c === 'function') {
    await nuxt_plugin_coophelper_1787988c(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_matomo_362ffbe5 === 'function') {
    await nuxt_plugin_matomo_362ffbe5(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_sentryconfig_2fbb3908 === 'function') {
    await nuxt_plugin_sentryconfig_2fbb3908(app.context, inject)
  }

  if (process.client && typeof nuxt_plugin_vuemultiselect_f82b3d8c === 'function') {
    await nuxt_plugin_vuemultiselect_f82b3d8c(app.context, inject)
  }

  // If server-side, wait for async component to be resolved first
  if (process.server && ssrContext && ssrContext.url) {
    await new Promise((resolve, reject) => {
      router.push(ssrContext.url, resolve, () => {
        // navigated to a different route in router guard
        const unregister = router.afterEach(async (to, from, next) => {
          ssrContext.url = to.fullPath
          app.context.route = await getRouteData(to)
          app.context.params = to.params || {}
          app.context.query = to.query || {}
          unregister()
          resolve()
        })
      })
    })
  }

  return {
    app,
    store,
    router
  }
}

export { createApp, NuxtError }
